import { useEffect, useState } from 'react'
import { Check, X, Loader2, ShieldAlert, Bot } from 'lucide-react'

import { Card, CardContent } from '@/ui/card'
import { Button } from '@/ui/button'
import { cn } from '@/core/lib/utils'
import { useIsMounted } from '@/core/hooks/useIsMounted'

import { useAgentStore } from './stores/agentStore'

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  completed: 'Completed',
}

const RISK_COLORS: Record<string, string> = {
  low: 'bg-green-100 text-green-700 border-green-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  critical: 'bg-red-100 text-red-700 border-red-200',
}

export function ApprovalsPage() {
  const mounted = useIsMounted()
  const { runs, runsLoading, runsError, loadRuns, approveRun, rejectRun } = useAgentStore()
  const [filter, setFilter] = useState<'all' | 'pending'>('pending')
  const [actingId, setActingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      if (!mounted.current) {return}
      await loadRuns(filter === 'pending' ? { status: 'pending' } : {})
    })()
  }, [filter, loadRuns])

  const filtered = filter === 'all' ? runs : runs.filter((r) => r.status === 'pending')
  const pendingCount = runs.filter((r) => r.status === 'pending').length

  async function handleApprove(id: string) {
    setActionError(null)
    setActingId(id)
    try {
      await approveRun(id)
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve run.')
    } finally {
      setActingId(null)
    }
  }

  async function handleReject(id: string) {
    setActionError(null)
    setActingId(id)
    try {
      await rejectRun(id)
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to reject run.')
    } finally {
      setActingId(null)
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-navy">AI Approvals</h2>
          <p className="text-sm text-slate-500">
            {pendingCount} pending approval{pendingCount !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {(['pending', 'all'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'text-sm px-3 py-1.5 rounded-lg border transition-colors',
                filter === f
                  ? 'bg-navy text-white border-navy'
                  : 'border-slate-150 text-slate-500 hover:border-navy'
              )}
            >
              {f === 'pending' ? 'Pending' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {actionError && (
        <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{actionError}</p>
      )}

      {/* Approval list */}
      {runsLoading ? (
        <Card>
          <CardContent className="py-16 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-slate-500">Loading approvals…</p>
          </CardContent>
        </Card>
      ) : runsError ? (
        <Card>
          <CardContent className="py-12 text-center text-red-600 text-sm">{runsError}</CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 flex flex-col items-center gap-3">
            <ShieldAlert className="w-10 h-10 text-slate-150" />
            <p className="text-sm text-slate-500">No approvals found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((run) => {
            const draft = (run.outputData?.draft_reply as string) || (run.outputData?.draft_email as string) || (run.outputData?.llm_response as string) || ''
            const isPending = run.status === 'pending'
            return (
              <Card key={run.id} className={cn('transition-colors', isPending ? 'border-l-4 border-l-primary' : '')}>
                <CardContent className="p-4">
                  <div className="flex flex-col gap-3">
                    {/* Top row: meta */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-medium text-navy">{run.agentId || run.actionType}</span>
                        <span className={cn('text-xs px-2 py-0.5 rounded border', RISK_COLORS[run.riskLevel] || RISK_COLORS.low)}>
                          {run.riskLevel}
                        </span>
                        <span className={cn(
                          'text-xs px-2 py-0.5 rounded border',
                          run.status === 'pending' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                          run.status === 'approved' ? 'bg-green-50 text-green-600 border-green-200' :
                          run.status === 'rejected' ? 'bg-red-50 text-red-600 border-red-200' :
                          'bg-gray-50 text-gray-500 border-gray-200'
                        )}>
                          {STATUS_LABELS[run.status] || run.status}
                        </span>
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">
                        {new Date(run.createdAt).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    {/* Context */}
                    {run.inputData && (
                      <div className="text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2">
                        {!!run.inputData.sender && <span>From: {String(run.inputData.sender)}</span>}
                        {!!run.inputData.subject && <span className="ml-3">Subject: {String(run.inputData.subject)}</span>}
                        {!!run.inputData.target_type && (
                          <span>Target: {String(run.inputData.target_type)} #{String(run.inputData.target_id || '').slice(0, 8)}</span>
                        )}
                      </div>
                    )}

                    {/* Draft / Output */}
                    {draft && (
                      <div className="text-sm text-navy whitespace-pre-wrap bg-white border border-slate-150 rounded-md px-3 py-2 max-h-40 overflow-y-auto">
                        {draft}
                      </div>
                    )}

                    {/* Actions */}
                    {isPending && (
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReject(run.id)}
                          disabled={actingId === run.id}
                        >
                          {actingId === run.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(run.id)}
                          disabled={actingId === run.id}
                        >
                          {actingId === run.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          Approve
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
